import { Blueprint, InitType } from "../blueprint";
import { makeState, PostState } from "./state/post.state";
import { postActions } from "./state/post.actions";
import { InstanceState as InstanceState } from "../../instance-service/instance-state.service";
import { StateServices } from "../../instance-service/i-state-services";
import { Vector2, Vector3 } from "three";
import { postSelectors } from "./state/post.selector";
import { RenderServices } from "../../instance-service/i-render-services";
import { Container } from "pixi.js";
import { Surface } from "../../../surface/primitives/surface";
import { CameraState } from "src/app/state/camera/camera.state";
import { Entity } from "src/app/state/entities/entity.model";
import { selectWorldTick10s } from "src/app/state/world/world.selector";
// import { imagesPixi } from "../../blueprints/images/images.pixi";
import { PointerTracker } from "src/app/binst/inputs/primitives/pointer-tracker";
import { Rect } from "src/app/binst/primitives/rect";
import { entityActions } from "src/app/state/entities/entity.actions";
import { degToRad } from "three/src/math/MathUtils.js";
import { selectCameraState } from "src/app/state/camera/camera.selector";
import {InstanceService} from "../../instance-service/instance.service";
import {NetworkServices} from "../../instance-service/i-network-services";
import {IMetadata} from "../../instance-service/i-metadata";
import {Subscription} from "rxjs";
import {BlueprintContext} from "../i-blueprint-context";
import {BlueprintSurface} from "../blueprint-movable";
import {
  selectEntityById,
  selectImageComponent,
  selectReactionsComponent,
  selectTextComponent
} from "../../../../state/entities/entity.selector";
import {ImageProperties} from "../../../../state/entities/component-properties-dto/images.properties.dto";
import {ImageGallery} from "../../../rendering/pixi/drawables/image-gallery.pixi";
import {PixiObject} from "../../../rendering/pixi/primitives/pixi-object";
import moment from "moment";
import {BackgroundDrawable} from "../../../rendering/pixi/drawables/background.pixi";
import {ProfileDrawable} from "../../../rendering/pixi/drawables/profile.pixi";
import {TextBoxDrawable} from "../../../rendering/pixi/drawables/text-box.pixi";
import {SocialBarDrawable} from "../../../rendering/pixi/drawables/social-bar.pixi";
import {ReactionProperties} from "../../../../state/entities/component-properties-dto/reactions.properties.dto";
import {TouchFollower} from "../../../rendering/pixi/drawables/touch-follower.pixi";
import {selectFriendReactionsByEntityId} from "../../../../state/reactions/reactions.selector";
import {FriendReaction} from "../../../../state/reactions/reactions.model";


// interface PixiComponent {
//   view: Container;
//   update(data: any): void;
//   destroy(): void;
// }

export class PostBlueprint extends BlueprintSurface {
  override id: string;
  override type: string = "Image"
  override state: InstanceState<PostState, ReturnType<typeof postActions>, ReturnType<typeof postSelectors>, PostBlueprint>
  actions$: ReturnType<typeof postActions>
  selectors: ReturnType<typeof postSelectors>
  friendReactions: FriendReaction[]


  isOwners: boolean

  touchFollower: TouchFollower

  override async init(config: {
    initType: InitType,
    metadata: IMetadata
  } & BlueprintContext) {
    this.log("[post] Starting")
    await super.init(config)
    this.isOwners = config.metadata.isOwners
    this.state = makeState(this)
    this.actions$ = this.state.actions
    this.selectors = this.state.selectors
    this.touchFollower = new TouchFollower({render: false})
    this.createOrUpdateBackground(100, 100)
    this.setupReactiveBindings()
    this.log("[post] Handlers configured")
  }


  override destroy(): void {
    this.surface.destroy()
  }

  override makeSurface(config: { entity: Entity; initType: InitType; stateServices: StateServices; renderServices: RenderServices; }): Promise<Surface> {
    throw new Error("Method not implemented.");
  }

  private setupReactiveBindings() {
    this.subscriptions.push(
        this.blueprintContext.stateServices.store
            .select(selectEntityById({entityId: this.entity.id})).subscribe((entity) => {
              if (!entity) {
                return
              }
            this.entity = entity;
        })
    )
    this.subscriptions.push(
        this.blueprintContext.stateServices.store
            .select(selectImageComponent(this.entity.id))
            .subscribe(async (imageProps: ImageProperties[]) => {
              if (!imageProps) {
                return
              }
              this.log("[post] Got image props")
              await this.createOrUpdateImages(imageProps)
              await this.createOrUpdateProfile()
              await this.createOrUpdateSocialBar()
        })
    )
    this.subscriptions.push(
        this.blueprintContext.stateServices.store
            .select(selectTextComponent(this.entity.id)).subscribe((text: string) => {
              if (!text) {
                return
              }
              this.createOrUpdateText(text)
        })
    )
    this.subscriptions.push(
        this.blueprintContext.stateServices.store
            .select(selectReactionsComponent(this.entity.id)).subscribe((reactions: ReactionProperties) => {
              if (!reactions) {
                return
              }
              if (!this.components.has('socialBar')) {
                return
              }
              const component = this.components.get('socialBar') as SocialBarDrawable
              component.updateReactions(reactions)
        })
    )
    this.subscriptions.push(
        this.blueprintContext.stateServices.store
            .select(selectFriendReactionsByEntityId(this.entity.id)).subscribe((friendReactions: FriendReaction[]) => {
          if (!friendReactions) {
            return
          }
          this.createOrSetFriendReactions(friendReactions)
        })
    )
  }

  createOrSetFriendReactions(friendReactions?) {
    if (friendReactions) {
      this.friendReactions = friendReactions
    }
    if (!this.friendReactions) {
      return
    }
    if (!this.components.has('socialBar')) {
      return
    }
    const component = this.components.get('socialBar') as SocialBarDrawable
    component.updateFriendReactions(this.friendReactions)
  }

  async createOrUpdateProfile() {
    const hasProfile = Object.keys(this.components).includes('profile')
    if (!hasProfile) {
      this.components.set('profile', new ProfileDrawable({
        blueprintContext: this.blueprintContext,
        isMe: false
      }))
    }
    // Profile updates itself.
    await this.layout()
  }


  async createOrUpdateText(text: string) {
    if (!Object.keys(this.components).includes('textbox')) {
      this.components.set('textbox', new TextBoxDrawable({
        blueprintContext: this.blueprintContext,
        width: 600,
        height: 200,
        text: text
      }))
    }
    await this.layout()
  }

  async createOrUpdateImages(imageProps: ImageProperties[]) {
    // this.log("[post] Creating image gallery")
    if (!Object.keys(this.components).includes('images')) {
      this.components.set('images', new ImageGallery({
        id: "ImageGallery",
        blueprintContext: this.blueprintContext,
      }))
    }
    let imageGallery = this.components.get('images') as ImageGallery
    for (const imageProp of imageProps) {
      await imageGallery.addImage({
        name: 'img',
        fileId: imageProp.fileId
      })
    }
    // this.log("[post] Image gallery created")
    await this.layout()
  }

  async createOrUpdateSocialBar() {
    if (!Object.keys(this.components).includes('socialBar')) {
      this.components.set('socialBar', new SocialBarDrawable({
        blueprintContext: this.blueprintContext,
        isMe: false
      }))
      this.createOrSetFriendReactions()
    }
    await this.layout()
  }

  createOrUpdateBackground(width, height) {
    if (!this.components.has('background')) {
      let bkg = new BackgroundDrawable({
        id: "Background",
        color: '#161616',
        roundness: 25,
        width,
        height
      })
      this.components.set('background', bkg)
    }
  }

  async setupSurface(width: number, height: number) {
    let requiredComponents = ['textbox', 'profile', 'images', 'socialBar']
    if (!requiredComponents.every(component => this.components.has(component))) {
      return
    }
    this.components.get('background').setRect({
      left: 0, top: 0, width, height
    })
    this.log(`Width: ${width}, height: ${height}`)
    this.surface = await this.renderServices.surfaceService.create({
      left: this.entity.coord.x,
      top: this.entity.coord.y,
      width: width,
      height: height + 50 // Allow for social bar at bottom
    })
    this.log("[post] surface created")
    this.components.forEach(component => {
      this.surface.addPixiPrimitive(component)
    })
    this.surface.addPixiPrimitive(this.touchFollower)
    this.surface.getTouchPrimitive().onHover((async (pt) => {
      this.touchFollower.moveTo(pt.relPos)
      this.log("Hovered")
      this.surface.markForRender()
      return false
    }).bind(this))
    this.setupSurfaceMovementHandlers(this.surface, this.isOwners)
  }


  private async layout() {
    let width = 0
    // Simple vertical layout - customize as needed
    let yOffset = 13;
    let xPadding = 33
    const componentOrder = ['profile', 'textbox', 'images'];
    componentOrder.forEach(key => {
      const component = this.components.get(key);
      if (!component) return
      xPadding = 25
      if (key === 'profile') {
        xPadding = 13
      }
      if (component) {
        component.sprite.y = yOffset;
        component.sprite.x = xPadding;
        component.updateTouchBounds()
        width = Math.max(component.width + (xPadding * 2) , width)
        yOffset += component.height + 20;
      }
    });
    {
      const component = this.components.get('socialBar');
      if (component) {
        component.sprite.y = yOffset;
        component.sprite.x = width - component.width - 20;
        component.updateTouchBounds()
      }
    }
    if (
        (!this.surface) &&
        ['socialBar', 'profile', 'textbox', 'images'].every((k) => this.components.has(k))
    ) {
      await this.setupSurface(width, yOffset + 20)
    }
    return {height: yOffset + 20, width: width}
  }


  override updatePosition(config: { newPos: Vector3; }) {
    if (!this.isMoving) {
      this.surface.mesh.position.x = config.newPos.x;
      this.surface.mesh.position.y = config.newPos.y;
    }
  }

}
